import { IPlayer, IPlayerName } from '../../../../shared/types/player.type';
import { GameStatus } from './constants';
import { yardFields } from '../Board/Board';
import { io, Socket } from 'socket.io-client';

let sessionId: string;
let tokenPositions: IPlayer[];
let lastActivePlayer: IPlayer[];
let diceNumber: number = 1;
let playerSelection: {
  isPlayerSelected: boolean[];
  selectedPlayer: number | null;
} = {
  isPlayerSelected: [false, false, false, false],
  selectedPlayer: null,
};
let move: string = '';
let observer:
  | null
  | ((
      tokenPositions: IPlayer[],
      diceNumber: number,
      playerNames: IPlayerName[],
      move: string,
      changes: {
        text: string;
        from: [number, number] | [null, null];
        to: [number, number] | [null, null];
      }[],
      gameStatus: GameStatus,
      playerSelection: {
        isPlayerSelected: boolean[];
        selectedPlayer: number | null;
      },
      activePlayer: number | null,
      stateChangeId: number | null,
    ) => void) = null;
let playerNames: IPlayerName[] = [];
let changes: {
  text: string;
  from: [number, number] | [null, null];
  to: [number, number] | [null, null];
}[] = [];
let gameStatus: GameStatus = GameStatus.LOADING;
let activePlayer: null;
let stateChangeId: null;

const PORT = process.env.REACT_APP_EXTERNAL_SERVER_PORT || 8000;
const subdomain = process.env.REACT_APP_EXTERNAL_SERVER_SUBDOMAIN || 'backend';

const hostName = window.location.hostname;
let hostNameParts = hostName.split('.');
if (hostNameParts.length > 2) {
  hostNameParts[0] = subdomain;
}
const externalHostname = hostNameParts.join('.');

function emitChange() {
  if (socket?.connected) {
    gameStatus = GameStatus.OK;
  } else {
    gameStatus = GameStatus.LOADING;
  }
  observer!(
    tokenPositions,
    diceNumber,
    playerNames,
    move,
    changes,
    gameStatus,
    playerSelection,
    activePlayer,
    stateChangeId,
  );
}

let socket: Socket | null = null;

function initSocket() {
  if (socket) {
    socket.off('connect');
    socket.off('disconnect');
    socket.off('stateUpdate');
    socket.close();
  }

  socket = io(
    `${window.location.protocol === 'https:' ? 'wss://' + externalHostname : 'ws://' + window.location.hostname + ':' + PORT}`,
  );

  socket.on('connect', () => {
    socket?.emit('register');
    emitChange();
  });

  socket.on('disconnect', () => {
    console.error('Connection error!');
    emitChange();
  });

  socket.on('registered', (data) => {
    sessionId = data.id;
  });

  socket.on('stateUpdate', (data) => {
    const {
      tokenPositions: newTokenPositions,
      diceNumber: newDiceNumber,
      playerNames: newPlayerNames,
      lastActivePlayer: newLastActivePlayer,
      move: newMove,
      changes: newChange,
      playerSelection: newPlayerSelection,
      activePlayer: newActivePlayer,
      stateChangeId: newStateChangeId,
    } = data;

    if (newTokenPositions !== undefined) {
      tokenPositions = newTokenPositions;
    }

    // change in lastActivePlayer
    if (newLastActivePlayer !== undefined) {
      lastActivePlayer = newLastActivePlayer;
    }

    // change in diceNumber
    if (newMove !== undefined) {
      move = newMove;
    }

    if (move === 'RESTARTGAME') {
      tokenPositions = newTokenPositions;
      playerSelection = newPlayerSelection;
      activePlayer = newActivePlayer;
      stateChangeId = newStateChangeId;
      changes = [];
      emitChange();
      return;
    }

    // change in diceNumber
    if (newDiceNumber !== undefined) {
      diceNumber = newDiceNumber;
    }

    // change in playerNames
    if (newPlayerNames !== undefined) {
      playerNames = newPlayerNames;
    }

    // change in playerSelection
    if (newPlayerSelection !== undefined) {
      playerSelection = newPlayerSelection;
    }

    if (newActivePlayer !== undefined) {
      activePlayer = newActivePlayer;
    }

    if (newStateChangeId !== undefined) {
      stateChangeId = newStateChangeId;
    }

    if (newChange !== undefined) {
      if (changes.length >= 200) {
        // limit the array to 200 entries
        changes.shift();
      }
      if (newChange.movedToken !== null) {
        // change detected
        changes.push({
          text: `\${${lastActivePlayer}} moved a figure`,
          from: newChange.from,
          to: newChange.to,
        });
      }
      if (newChange.dice) {
        diceNumber = newDiceNumber;
        changes.push({
          text: `\${${lastActivePlayer}} casted the dice. Result is: ${diceNumber}`,
          from: [null, null],
          to: [null, null],
        });
      }
    }

    emitChange();
  });
}

export function observe(
  o: (
    tokenPositions: IPlayer[],
    diceNumber: number,
    playerNames: IPlayerName[],
    move: string,
    changes: {
      text: string;
      from: [number, number] | [null, null];
      to: [number, number] | [null, null];
    }[],
    gameStatus: GameStatus,
    playerSelection: {
      isPlayerSelected: boolean[];
      selectedPlayer: number | null;
    },
    activePlayer: number | null,
    stateChangeId: number | null,
  ) => void,
): void {
  if (observer) {
    throw new Error('Multiple observers not implemented.');
  }
  initSocket();
  observer = o;
  emitChange();
}

function moveTokenToYard(player: IPlayer) {
  const playerYard = yardFields[player.number];

  if (!playerYard) {
    console.error(`No yard fields found for player ID: ${player.number}`);
    return;
  }

  for (let field of playerYard) {
    if (
      !tokenPositions.some(
        (t) => t.position[0] === field[0] && t.position[1] === field[1],
      )
    ) {
      player.position = field as [number, number];
      move = 'MOVEPLAYER';
      emitChange();
      return;
    }
  }
}

function placeToken(
  newPosition: [number, number],
  props: { tokenPositions: IPlayer[]; currentPlayerId: number },
) {
  const currentPlayer = props.tokenPositions.find(
    (currentPlayer) =>
      currentPlayer.position[0] === newPosition[0] &&
      currentPlayer.position[1] === newPosition[1],
  );

  if (currentPlayer) {
    moveTokenToYard(currentPlayer);
  }

  const currentPlayerIndex = props.tokenPositions.findIndex(
    (currentPlayer) => currentPlayer.id === props.currentPlayerId,
  );

  if (currentPlayerIndex !== -1) {
    props.tokenPositions[currentPlayerIndex].position = newPosition;
  }
}

export function canMovePlayer(
  toX: number,
  toY: number,
  isEmpty: boolean,
  isAccessible: boolean,
): boolean {
  return !isEmpty && isAccessible && toX + toY >= 0;
}

export function movePlayer(toX: number, toY: number, id?: number) {
  if (
    id === undefined ||
    playerSelection.selectedPlayer === null ||
    ![0, 1, 2, 3].includes(playerSelection.selectedPlayer)
  ) {
    console.log('ERRRRRRR');
    return;
  }
  if (playerSelection.selectedPlayer !== activePlayer) {
    console.log('NOT YOUR TURN');
    return;
  }
  placeToken([toX, toY], {
    tokenPositions: tokenPositions,
    currentPlayerId: id,
  });
  tokenPositions.forEach((player) => {
    if (player.id === id) {
      player.position[0] = toX;
      player.position[1] = toY;
    }
  });
  move = 'MOVEPLAYER';
  emitChange();
  if (socket) {
    socket.emit('stateChange', { id: sessionId, tokenPositions, move: move });
  }
}

export function castDice() {
  if (
    playerSelection.selectedPlayer === null ||
    ![0, 1, 2, 3].includes(playerSelection.selectedPlayer)
  ) {
    console.log('ERRRRRRR');
    return;
  }
  if (playerSelection.selectedPlayer !== activePlayer) {
    console.log('NOT YOUR TURN');
    return;
  }
  diceNumber = Math.ceil(Math.random() * 6);
  move = 'CAST';
  if (socket) {
    socket.emit('stateChange', {
      id: sessionId,
      diceNumber: diceNumber,
      move: move,
    });
  }
}

export function moveActivePlayerIndicator() {
  if (socket) {
    socket.emit('stateChange', {
      id: sessionId,
      move: 'MOVEACTIVEPLAYERINDICATOR',
    });
  }
}

export const changePlayerNames = (newPlayerNames: IPlayerName[]): void => {
  if (socket) {
    socket.emit('stateChange', {
      id: sessionId,
      playerNames: newPlayerNames,
      move: 'CHANGEPLAYERNAMES',
    });
  }
};

export const changeSelectedPlayer = (newSelectedPlayer: number): void => {
  if (socket) {
    socket.emit('stateChange', {
      id: sessionId,
      selectedPlayer: newSelectedPlayer,
      move: 'CHANGESELECTEDPLAYER',
    });
  }
};

export function restartGame() {
  if (socket) {
    socket.emit('stateChange', { id: sessionId, move: 'RESTARTGAME' });
  }
}
